/**
 * The project's "Dashboards" tab: lists the project's dashboards with Build
 * and View links, and creates, renames, duplicates and deletes them. Reads
 * the project from the project store. Says when the project has no data yet
 * and, with a questionnaire, offers dummy data; marks dummy data.
 */
import React, { useState } from "react"
import { useNavigate } from "react-router-dom"
import { Alert, Button, Table, Dropdown } from "antd"
import type { MenuProps } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faEllipsis, faPlus, faEye, faPen } from "@fortawesome/free-solid-svg-icons"
import { useProjectStore } from "stores/projectStore"
import { ROUTES } from "constants/routes"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import EmptyState from "shared/ui/EmptyState/EmptyState.component"
import ConfirmationModal from "shared/ui/ConfimationModal/ConfimationModal.component"
import ExportModal from "shared/ui/ExportModal/ExportModal.component"
import { notify } from "shared/ui/Notifications/notify"
import { useDashboardList, useDashboardMutations } from "features/Dashboard/api/useDashboardFiles"
import { useProjectHasFile } from "features/DummyData/api/useDummyData"
import DummyBadge from "features/DummyData/components/DummyBadge.component"
import DummyDataPrompt from "features/DummyData/components/DummyDataPrompt.component"

type Prompt = { kind: "create" } | { kind: "rename"; from: string } | { kind: "duplicate"; from: string } | null
type Row = { name: string }

const PROMPT_COPY: Record<NonNullable<Prompt>["kind"], { title: string; action: string }> = {
  create: { title: "New dashboard", action: "Create" },
  rename: { title: "Rename dashboard", action: "Rename" },
  duplicate: { title: "Duplicate dashboard", action: "Duplicate" },
}

/**
 * @param props.dataOnly - The project has no questionnaire (a data-only or
 *   dashboard project): no dummy data to offer or mark.
 */
export const DashboardsTab: React.FC<{ dataOnly?: boolean }> = ({ dataOnly = false }) => {
  const project = useProjectStore((s) => s.project)
  const navigate = useNavigate()
  const list = useDashboardList(project.id, project.Created_by)
  // Undefined until the first answer: the message waits for it.
  const data = useProjectHasFile(project.id, project.Created_by, "dqd")
  const { create, rename, duplicate, remove, isSaving } = useDashboardMutations(project.id, project.Created_by)
  const [prompt, setPrompt] = useState<Prompt>(null)
  const [pendingDelete, setPendingDelete] = useState<string | null>(null)

  const names = list.data ?? []
  const exists = (name: string) => names.some((n) => n.toLowerCase() === name.toLowerCase())

  const onPromptConfirm = async (name: string): Promise<void> => {
    const trimmed = name.trim()
    if (!trimmed || !prompt) return
    // Renaming to the unchanged name is a no-op, not a clash with itself.
    if (prompt.kind === "rename" && trimmed === prompt.from) { setPrompt(null); return }
    if (exists(trimmed)) { notify.error("Name in use", `A dashboard called "${trimmed}" already exists.`); return }
    try {
      if (prompt.kind === "create") { await create(trimmed); navigate(ROUTES.DASHBOARD(project.id, trimmed)) }
      else if (prompt.kind === "rename") await rename(prompt.from, trimmed)
      else await duplicate(prompt.from, trimmed)
      setPrompt(null)
    } catch (e) { notify.error("Dashboard", e instanceof Error ? e.message : "Something went wrong.") }
  }

  const onDeleteConfirm = async (): Promise<void> => {
    if (!pendingDelete) return
    try {
      await remove(pendingDelete)
      setPendingDelete(null)
    } catch (e) { notify.error("Dashboard", e instanceof Error ? e.message : "Something went wrong.") }
  }

  const rowMenu = (name: string): MenuProps["items"] => [
    { key: "rename", label: "Rename", onClick: () => setPrompt({ kind: "rename", from: name }) },
    { key: "duplicate", label: "Duplicate", onClick: () => setPrompt({ kind: "duplicate", from: name }) },
    { type: "divider" },
    { key: "delete", label: "Delete", danger: true, onClick: () => setPendingDelete(name) },
  ]

  return (
    <div className="p-4 flex flex-col gap-3 h-full">
      <div className="flex items-center gap-2">
        <h2 className="text-base font-medium m-0">Dashboards</h2>
        <DummyBadge projectId={dataOnly ? undefined : project.id} />
        <div className="ml-auto">
          <DataQButton type="primary" onClick={() => setPrompt({ kind: "create" })}>
            <FontAwesomeIcon icon={faPlus} />New dashboard
          </DataQButton>
        </div>
      </div>
      {data.has === false && (
        <Alert
          type="info"
          showIcon
          message="This project has no data yet."
          action={
            dataOnly ? undefined : (
              <DummyDataPrompt projectId={project.id} owner={project.Created_by} onGenerated={data.refresh} />
            )
          }
        />
      )}
      {list.isError ? (
        <EmptyState
          message="Could not load the dashboards"
          subtitle={list.error instanceof Error ? list.error.message : "Unknown error"}
        />
      ) : names.length === 0 && !list.isLoading ? (
        <EmptyState message="No dashboards yet" subtitle="Create one to start building pages of live tables and charts." />
      ) : (
        <Table<Row>
          size="small" rowKey="name" loading={list.isLoading} pagination={false}
          dataSource={names.map((name) => ({ name }))}
          columns={[
            {
              title: "Name", dataIndex: "name",
              render: (name: string) => (
                <button type="button" className="text-left font-medium hover:underline" onClick={() => navigate(ROUTES.DASHBOARD(project.id, name))}>{name}</button>
              ),
            },
            {
              title: "", key: "actions", width: 220,
              render: (_: unknown, row: Row) => (
                <div className="flex gap-2 justify-end">
                  <DataQButton type="secondary" onClick={() => navigate(ROUTES.DASHBOARD(project.id, row.name))}><FontAwesomeIcon icon={faPen} className="mr-1" />Build</DataQButton>
                  <DataQButton type="secondary" onClick={() => navigate(ROUTES.DASHBOARD(project.id, row.name, "view"))}><FontAwesomeIcon icon={faEye} className="mr-1" />View</DataQButton>
                  <Dropdown menu={{ items: rowMenu(row.name) }} trigger={["click"]}>
                    <Button aria-label={`More actions for ${row.name}`} icon={<FontAwesomeIcon icon={faEllipsis} />} />
                  </Dropdown>
                </div>
              ),
            },
          ]}
        />
      )}
      {/* Mounted only while open, so each prompt starts from its own default name. */}
      {prompt && (
        <ExportModal
          title={PROMPT_COPY[prompt.kind].title}
          isVisible
          cancel={() => setPrompt(null)}
          confirm={(filename) => onPromptConfirm(filename)}
          ExportButtonText={PROMPT_COPY[prompt.kind].action}
          message="Dashboard name"
          defaultFilename={prompt.kind === "create" ? "" : prompt.from}
        />
      )}
      {pendingDelete !== null && (
        <ConfirmationModal
          title="Delete dashboard"
          isVisible
          message={`Delete "${pendingDelete}"? This cannot be undone.`}
          confirmButtonText="Delete" confirmButtonType="danger"
          cancel={() => setPendingDelete(null)}
          confirm={() => void onDeleteConfirm()}
        />
      )}
      {isSaving && <span className="text-xs text-gray-500">Saving...</span>}
    </div>
  )
}
